import { Popover } from 'antd';
import React from 'react';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import type { DataAttributes, PopperPlacement } from '@/components/utils/TypeUtils';

interface PopperProps {
  /** Pass in the child to which the popper will be applied */
  children: React.ReactNode;
  /** Popper content */
  content: React.ReactNode;
  /** Type of trigger to enable popper */
  trigger?: 'click' | 'hover';
  /** Popper placement */
  placement?: PopperPlacement;
  /** Whether to show an arrow pointing to the popper */
  showArrow?: boolean;
  /** Whether to show or hide the popper */
  open?: boolean;
  /** Specify an optional className to be applied to the container node */
  overlayClassName?: string;
  /** Style of the popper */
  overlayStyle?: React.CSSProperties;
  /** Style of the popper content */
  overlayInnerStyle?: React.CSSProperties;
  /** The data-* attribute is used to store custom data private to the page or application.   */
  dataAttributes?: DataAttributes;
  /** on click event */
  onClick?: (type: string) => void;
  /** To handle custom popover open/close */
  customOpenClose?: boolean;
  /** on popover open or close event */
  onOpenChange?: (type: boolean) => void;
}

const Popper: React.FC<PopperProps> = ({
  content,
  children,
  trigger,
  placement,
  showArrow,
  overlayClassName,
  dataAttributes,
  overlayInnerStyle,
  overlayStyle,
  onOpenChange,
  open = true,
  customOpenClose = false,
  ...rest
}) =>
  /**
   * Popovers can be opened or closed on 'hover' or 'click'. If we want to manually control the open or close state,
   * we should make use of the 'open' parameter in the Ant Design library popover.
   *
   * However, using a single instance of the popover for both scenarios is not possible.
   * Hence, separate instances are needed for each use case.
   */
  customOpenClose ? (
    <Popover
      rootClassName={overlayClassName}
      content={content}
      placement={placement}
      trigger={trigger}
      arrow={showArrow}
      open={open}
      onOpenChange={onOpenChange}
      overlayInnerStyle={overlayInnerStyle}
      overlayStyle={overlayStyle}
      {...rest}
    >
      <div {...getPrefixedDataAttributes(dataAttributes, 'Popper')}>{children}</div>
    </Popover>
  ) : (
    <Popover
      rootClassName={overlayClassName}
      content={content}
      placement={placement}
      trigger={trigger}
      arrow={showArrow}
      overlayInnerStyle={overlayInnerStyle}
      overlayStyle={overlayStyle}
      {...rest}
    >
      <div {...getPrefixedDataAttributes(dataAttributes, 'Popper')}>{children}</div>
    </Popover>
  );

Popper.displayName = 'Popper';

Popper.defaultProps = {
  trigger: 'hover',
  showArrow: false,
  placement: 'bottom',
};

export default Popper;
